import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, LogOut, User } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/EnterpriseAuthContext';

interface InactivityWarningModalProps {
  isOpen: boolean;
  remainingSeconds: number;
  onStayActive: () => void;
}

export function InactivityWarningModal({ 
  isOpen, 
  remainingSeconds, 
  onStayActive 
}: InactivityWarningModalProps) {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSignOutNow = async () => {
    await signOut('user_logout', { 
      reason: 'user_initiated_during_warning',
      warning_acknowledged: true 
    });
    // Navigate to login immediately after sign-out
    navigate('/login', { replace: true });
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md border-warning/20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/95"
      >
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-warning/10">
            <AlertTriangle className="h-6 w-6 text-warning" />
          </div>
          <DialogTitle className="text-xl font-semibold text-foreground">
            Session Timeout Warning
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Countdown Display */}
          <Card className="border-warning/20 bg-warning/5">
            <CardContent className="flex items-center justify-center p-6">
              <div className="text-center">
                <Clock className="mx-auto mb-2 h-8 w-8 text-warning" />
                <div className="text-3xl font-mono font-bold text-warning">
                  {formatTime(remainingSeconds)}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Time remaining
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Warning Message */}
          <div className="text-center space-y-2">
            <p className="text-foreground">
              You've been inactive for 23 minutes. For your security, you'll be automatically signed out in:
            </p>
            <p className="text-sm text-muted-foreground">
              This will sign you out from <strong>all devices</strong> for security reasons.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <Button 
              onClick={onStayActive}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              size="lg"
            >
              <User className="mr-2 h-4 w-4" />
              Stay Active
            </Button>
            
            <Button 
              onClick={handleSignOutNow}
              variant="outline"
              className="w-full border-destructive/20 text-destructive hover:bg-destructive/10"
              size="lg"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out Now
            </Button>
          </div>

          {/* Security Notice */}
          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              This is a security feature to protect your account from unauthorized access.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}